import React, { useState, useEffect, useRef } from 'react';
import './Home.css';
import homeVideo from '../assets/videos/mkd_home.mov';
import { collection, getDocs, query, where, /*optional*/ orderBy, limit, addDoc, serverTimestamp, Timestamp, updateDoc, doc } from "firebase/firestore";
import { db, app } from "../services/firebase";
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SEO from '../components/SEO'; 
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickL, pickCategoryLabel as categoryLabel } from '../utils/i18nContent';
import { trackGoogleAdsConversion } from '../utils/googleAds';
import { trackMetaRegistrationConversion } from '../utils/metaPixel';

const MiniCalendar = ({ year, month, highlights, selectedDate, onSelectDate, weekdaysShort }) => {
  const weeks = [];
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  let week = Array(firstDay.getDay()).fill(null);

  for (let d = 1; d <= lastDay.getDate(); d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) {
    week = [...week, ...Array(7 - week.length).fill(null)];
    weeks.push(week);
  }

  const header = Array.isArray(weekdaysShort) && weekdaysShort.length === 7
    ? weekdaysShort
    : ['S','M','T','W','T','F','S'];

  return (
    <div className="mini-calendar-grid">
      {header.map((d,i) => (
        <div key={i} className="mc-header">{d}</div>
      ))}

      {weeks.map((wk, wi) =>
        wk.map((day, di) => {
          const pad2 = (n) => n.toString().padStart(2, '0');
          const dateStr = day
            ? `${year}-${pad2(month + 1)}-${pad2(day)}`
            : null;
          const isHighlighted = day && highlights.includes(dateStr);
          const isSelected = dateStr === selectedDate;

          return (
            <div
              key={`${wi}-${di}`}
              className={`mc-cell ${isSelected ? 'mc-cell--selected' : ''} ${!day ? 'mc-cell--empty' : ''}`}
              onClick={() => {
                if (dateStr && isHighlighted) {
                  onSelectDate(dateStr);
                }
              }}
            >
              {day && <span className="mc-day">{day}</span>}
              {isHighlighted && <span className="mc-dot" />}
            </div>
          );
        })
      )}
    </div>
  );
};

const Home = () => {
  const { t, i18n } = useTranslation('home');
  const TRIAL_NAME = { fr: "Cours d'essai", en: "Trial class", nl: "Proefles" };
  const TRIAL_PRICE = { fr: "10€", en: "€10", nl: "€10" };
  const lang2 = (i18n.language || 'fr').slice(0,2);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  // Cours
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  // Sélection de date
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDateTime, setSelectedDateTime] = useState('');
  const [loadingDates, setLoadingDates] = useState(false);

  const formatDateForDisplay = (dateStr) => {
    const options = { weekday: 'short', day: 'numeric', month: 'short' };
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language || 'fr', options);
  };

  // Formulaire
  const [dateOfBirth, setdateOfBirth] = useState('');
  const [isParentRegistering, setIsParentRegistering] = useState(false);
  const [parentContact, setParentContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentContact(prev => ({ ...prev, [name]: value }));
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageConsent, setImageConsent] = useState(null); // null = pas encore répondu
  const [contactForm, setContactForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const groupDatesByMonth = (dates) => {
    const buckets = {};
    dates.forEach(dateStr => {
      const dt = new Date(dateStr);
      const y = dt.getFullYear();
      const m = dt.getMonth();
      const key = `${y}-${m}`;
      if (!buckets[key]) {
        buckets[key] = { year: y, month: m, dates: [] };
      }
      buckets[key].dates.push(dateStr);
    });
    return Object.values(buckets).map(({ year, month, dates }) => ({
      year,
      month,
      dates,
      monthLabel: new Date(year, month).toLocaleDateString(i18n.language || 'fr', { 
        month: 'long', 
        year: 'numeric' 
      })
    }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation minimale côté client
      if (!selectedCourse?.id) {
        alert(t('contact.selectCourse'));
        setIsSubmitting(false);
        return;
      }
      if (imageConsent === null) {
        alert(t('imageConsent.required', { defaultValue: "Merci d'indiquer si vous autorisez ou non l'utilisation de l'image (photo/vidéo)." }));
        setIsSubmitting(false);
        return;
      }

      const contactInfoSrc = isParentRegistering ? parentContact : contactForm;

      // 1) Parent (si coché)
      let parentRefId = null;
      if (isParentRegistering) {
        const parentRef = await addDoc(collection(db, 'users'), {
          firstName: parentContact.firstName || '',
          lastName : parentContact.lastName  || '',
          email    : parentContact.email     || '',
          phone    : parentContact.phone     || '',
          role     : 'Parent',
          registrationSource: 'site',
          createdAt: Timestamp.now()
        });
        parentRefId = parentRef.id;

        await addDoc(collection(db, 'parents'), {
          parentId : parentRefId,
          firstName: parentContact.firstName || '',
          lastName : parentContact.lastName  || '',
          email    : parentContact.email     || '',
          phone    : parentContact.phone     || '',
          children : [],
          registrationSource: 'site',
          createdAt: serverTimestamp()
        });
      }

      // 2) Utilisateur élève
      const studentUser = {
        firstName  : contactForm.firstName || '',
        lastName   : contactForm.lastName  || '',
        email      : (contactInfoSrc.email || ''),
        phone      : (contactInfoSrc.phone || ''),
        dateOfBirth: dateOfBirth ? Timestamp.fromDate(new Date(dateOfBirth)) : null,
        role       : isParentRegistering ? 'Élève' : 'Élève adulte',
        ...(parentRefId ? { parentId: parentRefId } : {}),
        registrationSource: 'site',
        createdAt  : Timestamp.now()
      };
      const studentUserRef = await addDoc(collection(db, 'users'), studentUser);

      // 3) Fiche "students"
      const studentDoc = {
        userId   : studentUserRef.id,
        firstName: contactForm.firstName || '',
        lastName : contactForm.lastName  || '',
        dateOfBirth,
        email    : (contactInfoSrc.email || ''),
        phone    : (contactInfoSrc.phone || ''),
        parentId : parentRefId ?? null,
        courseId : selectedCourse?.id ?? null,
        createdAt: serverTimestamp()
      };
      await addDoc(collection(db, 'students'), studentDoc);

      // 4) Inscription
      const registrationData = {
        plan       : 'essai',
        planName   : TRIAL_NAME[lang2] || TRIAL_NAME.fr,
        courseId   : selectedCourse?.id ?? null,
        courseTitle: selectedCourse
          ? pickL(selectedCourse.title_i18n ?? selectedCourse.title, i18n.language, 'fr')
          : '',
        selectedDate     : selectedDate || null,
        selectedDateTime : selectedDateTime || null,
        lang     : i18n.language || 'fr',
        locale   : i18n.language || 'fr',
        uiLang   : i18n.language || 'fr',
        persons  : [{
          firstName  : contactForm.firstName || '',
          lastName   : contactForm.lastName  || '',
          dateOfBirth: dateOfBirth || null
        }],
        contactInfo: {
          email: (contactInfoSrc.email || ''),
          phone: (contactInfoSrc.phone || ''),
          lang : i18n.language || 'fr'
        },
        insuranceIncluded: false,
        imageRightsConsent: imageConsent,
        paymentDetails: {
          baseAmount: 10,
          insuranceAmount: 0,
          totalAmount: 10,
          paymentMethod: 'mollie'
        },
        registrationType : isParentRegistering ? 'child' : 'adult',
        createdAt: serverTimestamp(),
        trialDeductible: true,
        trialPrice: 10,
        registrationSource: 'site',
        source: 'site'
      };

      const registrationRef = await addDoc(collection(db, 'registrations'), registrationData);
      trackGoogleAdsConversion();
      trackMetaRegistrationConversion({
        value: 10,
        planId: 'essai',
        courseIds: selectedCourse ? [selectedCourse.id] : []
      });

      // Paiement immédiat via Mollie — le site ne connecte pas les visiteurs,
      // on établit donc une session anonyme juste avant l'appel, uniquement
      // pour satisfaire l'authentification requise par la Cloud Function.
      const auth = getAuth(app);
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const functions = getFunctions(app, 'europe-west1');
      const createPayment = httpsCallable(functions, 'createPayment');
      const response = await createPayment({
        type: 'registration',
        refId: registrationRef.id,
        amount: 10,
        description: "Cours d'essai MKD Studio"
      });

      const checkoutUrl = response.data?.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return; // on quitte la page vers Mollie
      }

      alert(t('contact.paymentLinkError', { defaultValue: "Inscription enregistrée, mais le lien de paiement n'a pas pu être généré. Nous vous contacterons." }));
      // reset (même nettoyage que le cas de succès normal, cf. plus bas)
      setContactForm({ firstName: '', lastName: '', email: '', phone: '' });
      setParentContact({ firstName: '', lastName: '', email: '', phone: '' });
      setSelectedDateTime('');
      setImageConsent(null);
    } catch (error) {
      console.error("Erreur lors de l'inscription : ", error);
      alert(t('contact.alertError', { message: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effet pour forcer la lecture de la vidéo
  useEffect(() => {
    const tryPlayVideo = () => {
      if (videoRef.current) {
        videoRef.current.play()
          .then(() => {
            console.log("Video is playing");
            setVideoLoaded(true);
          })
          .catch(error => {
            console.log("Video play failed, trying again:", error);
            setTimeout(tryPlayVideo, 500);
          });
      }
    };

    tryPlayVideo();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const q = query(collection(db, "dance_courses"));
        const querySnapshot = await getDocs(q);

        const coursesData = querySnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          const allDays = (data.dates || []).map(d => d.day);
          const uniqueDays = Array.from(new Set(allDays));
          const dayLabels = uniqueDays.join(", ");
          return {
            id: docSnap.id,
            title_i18n: data.title_i18n ?? null,
            title: data.title ?? '',
            dates: (data.dates || []).map(d => d.date),
            dayLabels
          };
        });

        setCourses(coursesData);
      } catch (error) {
        console.error("Erreur cours:", error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, []);

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const q = query(
          collection(db, "blog_posts"),
          where("status", "==", "published")
        );

        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        const toDate = v =>
          v?.seconds ? new Date(v.seconds * 1000) :
          (typeof v === 'string' ? new Date(v) :
          (v instanceof Date ? v : null));

        items.sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));

        setArticles(items.slice(0, 3));
      } catch (err) {
        console.error("Erreur lors de la récupération des articles:", err);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    if (selectedCourse && selectedCourse.dates) {
      setLoadingDates(true);
      const today = new Date().toISOString().split('T')[0];
      const futureSlots = selectedCourse.dates
        .filter(iso => iso && iso.split('T')[0] >= today)
        .sort();
      const futureDays = [...new Set(futureSlots.map(iso => iso.split('T')[0]))];
      setAvailableDates(futureDays);
      if (selectedDate) {
        const match = futureSlots.find(iso => iso.startsWith(selectedDate));
        setSelectedDateTime(match || '');
      }
      setLoadingDates(false);
    }
  }, [selectedCourse]);

  const weekdaysShort = t('calendar.weekdaysShort', { returnObjects: true });
  const todayISO = new Date().toISOString().split('T')[0];

  // Récupérer les informations de paiement depuis les traductions
  // Note : l'ancien objet de traduction paymentInfo (instructions de virement)
  // n'est plus utilisé — le paiement se fait désormais via Mollie.

  return (
    <div className="home-container">
      <SEO 
        title={t('seo.title')}
        description={t('seo.description')}
        keywords="danse, cours de danse, Bruxelles, hip-hop, afro dance, breakdance"
      />

      <section className="hero-section">
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline 
          className="hero-video"
          onLoadedData={() => setVideoLoaded(true)}
          style={{ opacity: videoLoaded ? 1 : 0 }}
        >
          <source src={homeVideo} type="video/mp4" />
          Votre navigateur ne supporte pas les vidéos HTML5.
        </video>

        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">{t('hero.title')}</h1>
            <p className="hero-subtitle">{t('hero.subtitle')}</p>

            <div className="hero-actions">
              <button
                className="cta-button"
                onClick={() =>
                  document
                    .getElementById('contact-form-section')
                    .scrollIntoView({ behavior: 'smooth' })
                }
                aria-label={t('hero.cta')}
              >
                {t('hero.cta')}
              </button>
            </div>

            <p className="hero-disclaimer">{t('hero.disclaimer')}</p>
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-left">
              <h2>{t('about.title')}</h2>
              <p>{t('about.quote')}</p>
              <button className="cta-button">
                <Link to="/a-propos" style={{ color: 'inherit', textDecoration: 'none' }}>
                  {t('about.cta')}
                </Link>
              </button>
            </div>
            <div className="about-right">
              <div className="highlight-card">
                <h3>{t('about.initiativeBadge')}</h3>
                <h2 className="initiative-title">{t('about.initiativeTitle')}</h2>
                <p>{t('about.initiativeIntro')}</p>
                <ul>
                  {(t('about.initiativeBullets', { returnObjects: true }) || []).map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
                <button className="cta-button">
                  <Link to="/a-propos" style={{ color: 'inherit', textDecoration: 'none' }}>
                    {t('about.initiativeCta')}
                  </Link>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="passion-section">
        <div className="container">
          <h1>{t('schedule.title')}</h1>
          <p>{t('schedule.subtitle')}</p>
          <button className="cta-button">
            <Link to="/horaires" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t('schedule.cta')}
            </Link>
          </button>
        </div>
      </section>

      <section className="services-section">
        <div className="container">
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">👨‍🏫</div>
              <h3>{t('services.teachers.title')}</h3>
              <p>{t('services.teachers.desc')}</p>
            </div>

            <div className="service-card">
              <div className="service-icon">💃</div>
              <h3>{t('services.styles.title')}</h3>
              <p>{t('services.styles.desc')}</p>      
            </div>

            <div className="service-card">
              <div className="service-icon">💪</div>
              <h3>{t('services.fitness.title')}</h3>
              <p>{t('services.fitness.desc')}</p>      
            </div>

            <div className="service-card">
              <div className="service-icon">🧠</div>
              <h3>{t('services.mental.title')}</h3>
              <p>{t('services.mental.desc')}</p>      
            </div>
          </div>
        </div>
      </section>

      <section className="articles-section">
        <div className="container">
          <h2>{t('articles.title')}</h2>
          <div className="articles-grid">
            {(() => {
              if (loading) return <p>{t('articles.loading')}</p>;
              if (!articles.length) return <p>{t('articles.empty')}</p>;
              return articles.map((article) => {
                const title = pickL(article.title_i18n ?? article.title, i18n.language, 'fr');
                const excerpt = pickL(article.excerpt_i18n ?? article.excerpt, i18n.language, 'fr');
                const cat = categoryLabel(article.category, i18n.language);
                const bgStyle = article.coverImage ? { backgroundImage: `url(${article.coverImage})` } : {};
                return (
                  <div className="article-card" key={article.id}>
                    <div className="article-image">
                      <div className="image-placeholder" style={bgStyle}>
                        {cat}
                      </div>
                    </div>
                    <div className="article-content">
                      <h3>{title || '—'}</h3>
                      <p>{excerpt || "…"} </p>
                      <Link to={`/blog/${article.id}`}>{t('articles.readMore')}</Link>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>{t('testimonials.title')}</h2>
            <p>{t('testimonials.subtitle')}</p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"On aime bien votre énergie, votre façon d'enseigner et toutes les valeurs que vous essayez d'inculquer aux enfants. 
Vous n'êtes pas juste des profs de danse. 
Vous êtes des vraies coach de vie motivantes et inspirantes. 
❤ "</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar" style={{backgroundImage: "url('https://randomuser.me/api/portraits/women/44.jpg')"}}></div>
                <div className="author-info">
                  <h4>Christina</h4>
                  <p>Maman de Nayah</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>" Merciii a vous deux pour cette année, pour l'énergie que vous aviez dans nos cours et surtout la confiance que vous aviez en nous. Merci pour ce challenge."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar" style={{backgroundImage: "url('https://randomuser.me/api/portraits/men/32.jpg')"}}></div>
                <div className="author-info">
                  <h4>Thomas Dubois</h4>
                  <p>Élève en streetdance</p>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>"C'est très rare d'avoir des profs de danse qui soient aussi proches des élèves. Vous êtes incroyable, toujours là avec votre sourire et votre bonne humeur. Merci pour cette année où j'ai appris bcp de choses et où vous étiez là à prendre du temps pour nous demander si on allait vrmnt bien."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar" style={{backgroundImage: "url('https://randomuser.me/api/portraits/women/68.jpg')"}}></div>
                <div className="author-info">
                  <h4>Camille Leroy</h4>
                  <p>Élève de la formation crew</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact-form-section" className="contact-section">
        <div className="container">
          <h2>{t('contact.title')}</h2>
          <p>{t('contact.subtitle')}</p>
          
          <div className="trial-note">
            <p><strong>📝 Note :</strong> {t('contact.trialNote', { price: TRIAL_PRICE[lang2] || TRIAL_PRICE.fr })}</p>
          </div>

          <form className="contact-form" onSubmit={handleContactSubmit}>
            <div className="field-group">
              <label htmlFor="firstName" className="field-label">
                {t('contact.firstName')} <span className="req">*</span>
              </label>
              <input 
                id="firstName"
                type="text" 
                placeholder={t('contact.firstName')}
                required 
                name="firstName"
                autoComplete="given-name"
                value={contactForm.firstName}
                onChange={handleContactChange}
              />
            </div>

            <div className="field-group">
              <label htmlFor="lastName" className="field-label">
                {t('contact.lastName')} <span className="req">*</span>
              </label>
              <input 
                id="lastName"
                type="text" 
                placeholder={t('contact.lastName')}
                required 
                name="lastName"
                autoComplete="family-name"
                value={contactForm.lastName}
                onChange={handleContactChange}
              />
            </div>

            <div className="field-group">
              <label htmlFor="dob" className="field-label">
                {t('contact.dob')} <span className="req">*</span>
              </label>
              <input 
                id="dob"
                type="date" 
                placeholder={t('contact.dob')}
                required 
                value={dateOfBirth}
                onChange={(e) => setdateOfBirth(e.target.value)}
                autoComplete="bday"
                max={todayISO}
              />
            </div>

            <div className="field-group">
              <label htmlFor="email" className="field-label">
                {t('contact.email')} {!isParentRegistering && <span className="req">*</span>}
              </label>
              <input 
                id="email"
                type="email" 
                placeholder={t('contact.email')}
                required={!isParentRegistering}
                name="email"
                autoComplete="email"
                value={contactForm.email}
                onChange={handleContactChange}
              />
            </div>

            <div className="field-group">
              <label htmlFor="phone" className="field-label">
                {t('contact.phone')} {!isParentRegistering && <span className="req">*</span>}
              </label>
              <input 
                id="phone"
                type="tel" 
                placeholder={t('contact.phone')}
                required={!isParentRegistering}
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                value={contactForm.phone}
                onChange={handleContactChange}
              />
            </div>

            <div className="field-group">
              <label htmlFor="course" className="field-label">
                {t('contact.selectCourse')} <span className="req">*</span>
              </label>
              <select 
                id="course"
                required 
                onChange={(e) => {
                  const courseId = e.target.value;
                  const course = courses.find(c => c.id === courseId);
                  setSelectedCourse(course);
                  setSelectedDate('');
                  setSelectedDateTime('');
                }}
                value={selectedCourse?.id || ''}
              >
                <option value="">{t('contact.selectCourse')}</option>
                {loadingCourses ? (
                  <option>{t('contact.loading')}</option>
                ) : (
                  courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {pickL(course.title_i18n ?? course.title, i18n.language, 'fr')} – {course.dayLabels}
                    </option>
                  ))
                )}
              </select>
            </div>

            {selectedCourse && (
              <div className="calendar-section">
                <h4>
                  {t('calendar.availableFor', {
                    course: pickL(selectedCourse.title_i18n ?? selectedCourse.title, i18n.language, 'fr')
                  })}
                </h4>

                {loadingDates ? (
                  <p>{t('calendar.loading')}</p>
                ) : availableDates.length > 0 ? (
                  <>
                    <div className="mini-calendar">
                      {groupDatesByMonth(availableDates).map(({ year, month, dates, monthLabel }, idx) => (
                        <div key={idx} className="month-group">
                          <h5>{monthLabel}</h5>
                          <MiniCalendar
                            year={year}
                            month={month}
                            highlights={dates}
                            selectedDate={selectedDate}
                            onSelectDate={(d) => {
                              setSelectedDate(d);
                              const match = (selectedCourse?.dates || []).find(iso => iso.startsWith(d));
                              setSelectedDateTime(match || '');
                            }}
                            weekdaysShort={weekdaysShort}
                          />
                        </div>
                      ))}
                    </div>

                    {selectedDate && (
                      <div className="selected-date-info">
                        <p className="sdi-title">
                          {pickL(selectedCourse.title_i18n ?? selectedCourse.title, i18n.language, 'fr')}
                        </p>
                        <p className="sdi-date">{formatDateForDisplay(selectedDate)}</p>
                        <p className="sdi-price">
                          {TRIAL_NAME[lang2] || TRIAL_NAME.fr}: {TRIAL_PRICE[lang2] || TRIAL_PRICE.fr}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <p>{t('calendar.none')}</p>
                )}
              </div>
            )}

            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={isParentRegistering}
                onChange={(e) => setIsParentRegistering(e.target.checked)}
              />
              {t('contact.parentToggle')}
            </label>

            {isParentRegistering && (
              <div className="parent-fields">
                <div className="field-group">
                  <label htmlFor="parentFirstName" className="field-label">
                    {t('contact.parentFirstName')} <span className="req">*</span>
                  </label>
                  <input 
                    id="parentFirstName"
                    type="text" 
                    placeholder={t('contact.parentFirstName')}
                    required 
                    name="firstName"
                    autoComplete="given-name"
                    value={parentContact.firstName}
                    onChange={handleParentChange}
                  />
                </div>

                <div className="field-group">
                  <label htmlFor="parentLastName" className="field-label">
                    {t('contact.parentLastName')} <span className="req">*</span>
                  </label>
                  <input 
                    id="parentLastName"
                    type="text" 
                    placeholder={t('contact.parentLastName')}
                    required 
                    name="lastName"
                    autoComplete="family-name"
                    value={parentContact.lastName}
                    onChange={handleParentChange}
                  />
                </div>

                <div className="field-group">
                  <label htmlFor="parentEmail" className="field-label">
                    {t('contact.parentEmail')} <span className="req">*</span>
                  </label>
                  <input 
                    id="parentEmail"
                    type="email" 
                    placeholder={t('contact.parentEmail')}
                    required 
                    name="email"
                    autoComplete="email"
                    value={parentContact.email}
                    onChange={handleParentChange}
                  />
                </div>

                <div className="field-group">
                  <label htmlFor="parentPhone" className="field-label">
                    {t('contact.parentPhone')} <span className="req">*</span>
                  </label>
                  <input 
                    id="parentPhone"
                    type="tel" 
                    placeholder={t('contact.parentPhone')}
                    required 
                    name="phone"
                    autoComplete="tel"
                    inputMode="tel"
                    value={parentContact.phone}
                    onChange={handleParentChange}
                  />
                </div>
              </div>
            )}

            <div className="field-group">
              <label className="field-label">
                {t('imageConsent.title', { defaultValue: "Droit à l'image" })} <span className="req">*</span>
              </label>
              <p style={{ fontSize: '0.85rem', margin: '4px 0 10px' }}>
                {t('imageConsent.description', { defaultValue: "Dans le cadre de nos cours et événements, il nous arrive de prendre des photos ou vidéos publiées ensuite sur nos réseaux sociaux (Instagram, Facebook) à des fins de communication. Merci d'indiquer si vous autorisez l'utilisation de l'image (photo/vidéo) de la personne inscrite à ces fins." })}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="imageConsent"
                    checked={imageConsent === true}
                    onChange={() => setImageConsent(true)}
                    required
                  />
                  {t('imageConsent.yes', { defaultValue: "Oui, j'autorise" })}
                </label>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="imageConsent"
                    checked={imageConsent === false}
                    onChange={() => setImageConsent(false)}
                    required
                  />
                  {t('imageConsent.no', { defaultValue: "Non, je n'autorise pas" })}
                </label>
              </div>
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('contact.sending') : t('contact.submit')} - {TRIAL_PRICE[lang2] || TRIAL_PRICE.fr}
            </button>
           
            <div className="payment-info-card">
              <h3>{t('contact.paymentTitle', { defaultValue: 'Paiement' })}</h3>
              <p>
                {t('contact.paymentMollieNote', { defaultValue: 'Paiement en ligne sécurisé (carte, Bancontact) via Mollie — vous serez redirigé(e) vers la page de paiement juste après validation de ce formulaire.' })}
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;